import { frameId, type AssetManifest } from "../assets/manifest.ts";
import type { TileRef } from "../document/types.ts";
import { tilesetHeading, type EditorCatalogue } from "./catalogue.ts";
import { packOf } from "./packs.ts";
import { packNamesOf, searchWords } from "./prop-listing.ts";
import { frameLabel } from "./transitions.ts";

/**
 * The frames a state's frame picker offers (ADR-070's amendment "a state may rest on a frame of any tileset",
 * brief 6.3 item 3). A further state of a behaviour prop rests on a frame drawn where the placed frame is
 * drawn, so the frames that fit are those of the placed frame's width and height, still or strip, in whatever
 * tileset the world lists: equal size keeps the anchor, since a prop stands on the bottom centre of its frame.
 * They are offered nearest first: the game's suggestion, the placed frame's own sheet with each strip's inner
 * frames after its first (a state may rest on a strip's last frame, which the catalogue does not list), the
 * strips the prop's transitions play, wherever they come from, then every other tileset's frames of that
 * size, tileset by tileset. Nothing here knows what a state means.
 */

/**
 * A frame the picker offers: the frame, how the inspector names it, whether it is the game's suggestion, and
 * the tileset it is listed under, null for the suggestion, the placed sheet's frames and the played strips,
 * which need no heading.
 */
export interface FrameFit {
  readonly frame: TileRef;
  readonly name: string;
  readonly suggested: boolean;
  readonly sheet: string | null;
}

/**
 * A run of offered frames the picker lists together: the frames nearest the prop under no label, then each
 * other tileset's under the tileset's heading.
 */
export interface FrameSection {
  readonly sheet: string | null;
  readonly label: string | null;
  readonly fits: readonly FrameFit[];
}

/**
 * A section as a picker draws it: `fits` are the frames it draws a thumbnail for, `count` how many frames the
 * section holds, so `count - fits.length` are held back until a search narrows the list.
 */
export interface DrawnSection extends FrameSection {
  readonly count: number;
}

/**
 * A strip frame's id as a tile of the given sheet's default variant.
 *
 * @param id The frame id.
 * @param tileset The sheet the strip belongs to.
 * @returns The frame, or undefined when the id names another sheet or variant.
 */
export function tileOf(id: string, tileset: string): TileRef | undefined {
  const tile = Number(id.slice(id.lastIndexOf("/") + 1));
  return Number.isInteger(tile) && tile >= 0 && frameId(tileset, null, tile) === id ? { tileset, tile } : undefined;
}

/**
 * A frame and the inner frames of the strip it starts, in the strip's order.
 *
 * @param manifest The editor's asset manifest.
 * @param frame The frame.
 * @returns The frame, then the strip's other frames in its sheet.
 */
function withStrip(manifest: AssetManifest, frame: TileRef): TileRef[] {
  const animation = manifest.animations[frameId(frame.tileset, null, frame.tile)];
  const inner = animation === undefined ? [] : animation.frames.slice(1).flatMap((id) => tileOf(id, frame.tileset) ?? []);
  return [frame, ...inner];
}

/**
 * The catalogue's prop frames of every tileset but one, tileset by tileset in the order the catalogue first
 * names each, each strip's inner frames after its first.
 *
 * @param manifest The editor's asset manifest, for the strips.
 * @param catalogue The world's catalogue.
 * @param placedSheet The tileset left out.
 * @param listed The tilesets the world lists, or undefined to take every tileset of the catalogue.
 * @returns The frames.
 */
function otherSheets(manifest: AssetManifest, catalogue: EditorCatalogue, placedSheet: string, listed: ReadonlySet<string> | undefined): TileRef[] {
  const bySheet = new Map<string, TileRef[]>();
  for (const entry of catalogue.props) {
    if (entry.tileset === placedSheet || (listed !== undefined && !listed.has(entry.tileset))) {
      continue;
    }
    const frames = bySheet.get(entry.tileset) ?? [];
    frames.push(...withStrip(manifest, { tileset: entry.tileset, tile: entry.tile }));
    bySheet.set(entry.tileset, frames);
  }
  return [...bySheet.values()].flat();
}

/**
 * The frames that fit a placed frame, as the picker lists them: the suggestion first, then the placed sheet's
 * frames of the placed frame's size in the catalogue's order with each strip's inner frames after its first,
 * then those of the given strips, then those of every other tileset the world lists, tileset by tileset in the
 * order the catalogue first names each, a strip there offered as one of the placed sheet is. Each frame is
 * offered once, where it first comes.
 *
 * @param manifest The editor's asset manifest, for sizes and strips.
 * @param catalogue The world's catalogue.
 * @param placed The prop's own frame.
 * @param suggestion The game's suggestion for the state, listed first whatever its size, if any.
 * @param strips The first frames of the strips the prop's transitions play.
 * @param listed The ids of the tilesets the world's document lists, which are the only ones a command lets a
 *   state name; a catalogue built while the world held another pack may describe more. Undefined offers every
 *   tileset of the catalogue.
 * @returns The frames, none when the placed frame is not in the manifest.
 */
export function fittingFrames(manifest: AssetManifest, catalogue: EditorCatalogue, placed: TileRef, suggestion: TileRef | undefined, strips: readonly TileRef[], listed?: ReadonlySet<string>): FrameFit[] {
  const size = manifest.frames[frameId(placed.tileset, null, placed.tile)];
  if (size === undefined) {
    return [];
  }
  const fits = (frame: TileRef): boolean => {
    const rect = manifest.frames[frameId(frame.tileset, null, frame.tile)];
    return rect !== undefined && rect.w === size.w && rect.h === size.h;
  };
  const sheet = catalogue.props.filter((entry) => entry.tileset === placed.tileset).flatMap((entry) => withStrip(manifest, { tileset: entry.tileset, tile: entry.tile }));
  const played = strips.flatMap((strip) => withStrip(manifest, strip));
  const seen = new Set<string>();
  const offered: FrameFit[] = [];
  const offer = (frame: TileRef, suggested: boolean, under: string | null): void => {
    const id = frameId(frame.tileset, null, frame.tile);
    if (!seen.has(id)) {
      seen.add(id);
      offered.push({ frame: { tileset: frame.tileset, tile: frame.tile }, name: frameLabel(catalogue, manifest, frame), suggested, sheet: under });
    }
  };
  if (suggestion !== undefined) {
    offer(suggestion, true, null);
  }
  for (const frame of [...sheet, ...played]) {
    if (fits(frame)) {
      offer(frame, false, null);
    }
  }
  for (const frame of otherSheets(manifest, catalogue, placed.tileset, listed)) {
    if (fits(frame)) {
      offer(frame, false, frame.tileset);
    }
  }
  return offered;
}

/**
 * A tileset's label over its section: the tileset's heading (`tilesetHeading` of the catalogue's name for it,
 * or of its id where the catalogue describes no such tileset), after its pack's display name and " / " when
 * the catalogue names the pack, since packs repeat tileset names.
 *
 * @param sheet The tileset id.
 * @param catalogue The world's catalogue.
 * @param packs The catalogue's pack names by pack id.
 * @returns The label.
 */
export function sheetLabel(sheet: string, catalogue: EditorCatalogue, packs: ReadonlyMap<string, string>): string {
  const heading = tilesetHeading(catalogue.tilesets.find((tileset) => tileset.id === sheet)?.name ?? sheet);
  const pack = packs.get(packOf(sheet) ?? "");
  return pack === undefined ? heading : `${pack} / ${heading}`;
}

/**
 * The sections a picker lists offered frames under, in the order offered: the frames nearest the prop under
 * no label, then each other tileset's under its label (`sheetLabel`). A section with no frame, as after a
 * search, is left out.
 *
 * @param fits The frames offered, or those a search kept.
 * @param catalogue The world's catalogue, for the tilesets' and the packs' names.
 * @returns The sections.
 */
export function frameSections(fits: readonly FrameFit[], catalogue: EditorCatalogue): FrameSection[] {
  const packs = packNamesOf(catalogue);
  const sections: FrameSection[] = [];
  const bySheet = new Map<string | null, FrameFit[]>();
  for (const fit of fits) {
    const known = bySheet.get(fit.sheet);
    if (known !== undefined) {
      known.push(fit);
      continue;
    }
    const held = [fit];
    bySheet.set(fit.sheet, held);
    sections.push({ sheet: fit.sheet, label: fit.sheet === null ? null : sheetLabel(fit.sheet, catalogue, packs), fits: held });
  }
  return sections;
}

/**
 * The sections of offered frames as a picker draws them when it draws at most so many thumbnails: every
 * section is kept with the count of the frames it holds, and the thumbnails are shared out so each section
 * draws some. Each section first takes an equal share (the most divided by the sections, at least one), a
 * section holding fewer taking only what it holds; what is left over then goes to the sections in order, so
 * the frames nearest the prop get it first. A section draws its first frames in the order offered. The chosen
 * frame is always drawn: when the cut would leave it out it follows its section's drawn frames, so the total
 * may pass the most by that one, and by the sections beyond the most when there are more sections than
 * thumbnails.
 *
 * @param fits The frames offered, or those a search kept.
 * @param catalogue The world's catalogue, for the labels.
 * @param most The most thumbnails to draw, a whole number of at least one.
 * @param chosen The frame the state rests on, if any.
 * @returns The sections, each with the frames it draws and how many it holds.
 */
export function drawnSections(fits: readonly FrameFit[], catalogue: EditorCatalogue, most: number, chosen?: TileRef): DrawnSection[] {
  const sections = frameSections(fits, catalogue);
  if (sections.length === 0) {
    return [];
  }
  const share = Math.max(1, Math.floor(most / sections.length));
  const takes = sections.map((section) => Math.min(section.fits.length, share));
  let spare = most - takes.reduce((sum, take) => sum + take, 0);
  for (let index = 0; index < sections.length && spare > 0; index += 1) {
    const extra = Math.min(spare, (sections[index] as FrameSection).fits.length - (takes[index] as number));
    takes[index] = (takes[index] as number) + extra;
    spare -= extra;
  }
  return sections.map((section, index) => {
    const take = takes[index] as number;
    const kept = chosen === undefined ? undefined : section.fits.slice(take).find((fit) => fit.frame.tileset === chosen.tileset && fit.frame.tile === chosen.tile);
    return { sheet: section.sheet, label: section.label, fits: kept === undefined ? section.fits.slice(0, take) : [...section.fits.slice(0, take), kept], count: section.fits.length };
  });
}

/**
 * The frames a search keeps, in the order offered: each word of the search must be in the frame's name,
 * whatever the case, as the prop palette's search reads its words.
 *
 * @param fits The frames offered.
 * @param search The text typed.
 * @returns The frames kept; every one for an empty or blank search.
 */
export function searchFrames(fits: readonly FrameFit[], search: string): FrameFit[] {
  const words = searchWords(search);
  return fits.filter((fit) => {
    const name = fit.name.toLowerCase();
    return words.every((word) => name.includes(word));
  });
}
